
import { api, upload } from '@/services/api';
import {Module} from 'vuex';
import {RootState} from '@/types';
import { InjuryForm, IInjuryForm} from '@/classes/InjuryForm';
import InjuryDocument from '@/classes/InjuryDocument';

export interface InjuryState {
    injuryForms: InjuryForm[];
    injuryDocuments: InjuryDocument[];
}

export const InjuryStore: Module<InjuryState, RootState> = {
    namespaced: true,

    state: {
        injuryForms: [],
        injuryDocuments: []
    },

    getters: {
        getInjuryForms: (state): InjuryForm[] => {
            if (state.injuryForms !== undefined) {
                return state.injuryForms;
            } else {
                return [];
            }
        },
        getInjuryFormById: (state) => (id: number): InjuryForm | undefined => {
            const index: number = state.injuryForms.findIndex((form: InjuryForm) => form.id === Number(id));
            if (index !== -1) {
                return state.injuryForms[index];
            } else {
                return undefined;
            }
        },
        getInjuryDocuments: (state): InjuryDocument[] => {
            if (state.injuryDocuments !== undefined) {
                return state.injuryDocuments;
            } else {
                return [];
            }
        },
        getInjuryDocumentById: (state) => (id: number): InjuryDocument | undefined => {
            const index: number = state.injuryDocuments.findIndex((document: InjuryDocument) => document.id === Number(id));
            if (index !== -1) {
                return state.injuryDocuments[index];
            } else {
                return undefined;
            }
        }
    },

    mutations: {
        setInjuryForms(state, injuryForms: InjuryForm[]) {
            state.injuryForms = injuryForms;
        },
        setInjuryDocuments(state, injuryDocuments: InjuryDocument[]) {
            state.injuryDocuments = injuryDocuments;
        }
    },

    actions: {
        loadInjuryForms({ commit, state }, forceRefresh: boolean) {
            return new Promise((resolve, reject) => {
                if (state.injuryForms.length === 0 || forceRefresh) {
                    api().get('injuryforms')
                    .then((response) => {
                        if (response.data.success) {
                            const formsJSON = response.data.forms;
                            const forms: InjuryForm[] = [];
                            formsJSON.forEach((element: any) => {
                                const injuryReportJSON: JSON = element.jsonB ? JSON.parse(element.jsonB) : undefined;
                                const form: InjuryForm = new InjuryForm(
                                    {...injuryReportJSON, ...element} as IInjuryForm);
                                if (form.injuredPerson && form.injuredPerson === 'student') {
                                    if (form.student && form.student.studentId === undefined) {
                                        form.student.studentId = formsJSON.studentId as number;
                                    }
                                }
                                forms.push(form);
                            });
                            commit('setInjuryForms', forms);
                        }
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    });
                } else {
                    resolve();
                }
            });
        },
        addInjuryForm({ dispatch }, form: InjuryForm) {
            return new Promise((resolve, reject) => {
                api().post('injuryform', {
                    form: {
                        action: form.action,
                        injuredPerson: form.injuredPerson,
                        injuryDate: form.injury !== undefined ? form.injury.date : undefined,
                        injuryReportType: form.fieldCoordinatorReview !== undefined ? form.fieldCoordinatorReview.reportType
                        : undefined,
                        injuryType: form.fieldCoordinatorReview !== undefined ? form.fieldCoordinatorReview.injuryType
                            : undefined,
                        studentId: form.student !== undefined ? form.student.studentId : undefined,
                        userId: form.employee !== undefined ? form.employee.userId : undefined,
                        jobCorpsCenterId: form.jobCorpsCenterId,
                        jsonB: JSON.stringify(form)
                    }
                })
                .then((response) => {
                    if (response.data.success) {
                        resolve(response.data);
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        editInjuryForm({ dispatch }, form: InjuryForm) {
            return new Promise((resolve, reject) => {
                api().patch('injuryform', {
                    form: {
                        id: form.id,
                        action: form.action,
                        injuredPerson: form.injuredPerson,
                        injuryDate: form.injury !== undefined ? form.injury.date : undefined,
                        injuryReportType: form.fieldCoordinatorReview !== undefined ? form.fieldCoordinatorReview.reportType
                        : undefined,
                        injuryType: form.fieldCoordinatorReview !== undefined ? form.fieldCoordinatorReview.injuryType
                            : undefined,
                        studentId: form.student !== undefined ? form.student.studentId : undefined,
                        userId: form.employee !== undefined ? form.employee.userId : undefined,
                        jobCorpsCenterId: form.jobCorpsCenterId,
                        jsonB: JSON.stringify(form)
                    }
                })
                .then((response) => {
                    if (response.data.success) {
                        resolve(response.data);
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        loadInjuryDocuments({ commit, state }, forceRefresh: boolean) {
            return new Promise((resolve, reject) => {
                if (state.injuryDocuments.length === 0 || forceRefresh) {
                    api().get('injurydocuments')
                    .then((response) => {
                        if (response.data.success) {
                            const documentsJSON = response.data.documents;
                            const documents: InjuryDocument[] = [];
                            documentsJSON.forEach((element: any) => {
                                const document: InjuryDocument = element as InjuryDocument;
                                documents.push(document);
                            });
                            commit('setInjuryDocuments', documents);
                        }
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    });
                } else {
                    resolve();
                }
            });
        },
        addInjuryDocument({}, document: InjuryDocument) {
            return  new Promise((resolve, reject) => {
            upload('/injurydocument', document)
                .then((response) => {
                    if ( response.data.success) {
                        resolve(response.data.document);
                    } else {
                        reject(response.data.message);
                    }
                })
                .catch((err) => {
                    reject(err);
                });
            });
        },
        removeInjuryDocument({}, injuryFormId: number) {
            return  new Promise((resolve, reject) => {
                api().delete('/injurydocument/' + injuryFormId)
                .then((r) => {resolve(r); })
                .catch((r) => {reject(r); });
            });
        }
    }
};
